
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ColumnConfigItem, TableType } from './types';
import { PLACEHOLDER_COLUMN_CONFIG } from '@/hooks/placeholder-data/placeholderData';

export interface UseColumnConfigResult {
  columnConfig: ColumnConfigItem[];
  isLoading: boolean;
  error: unknown;
  isAdmin: boolean;
  updateColumnConfig: (newConfig: ColumnConfigItem[]) => Promise<boolean>;
}

export function useColumnConfig(tableType: TableType): UseColumnConfigResult {
  const [isAdmin, setIsAdmin] = useState<boolean>(true); // Default to admin for prototype
  
  // Use placeholder data since workspace context is removed
  const { data: columnConfig, isLoading, error, refetch } = useQuery<ColumnConfigItem[]>({
    queryKey: ['columnConfig', tableType],
    queryFn: async (): Promise<ColumnConfigItem[]> => {
      try {
        // Return placeholder data since workspace context is removed
        return PLACEHOLDER_COLUMN_CONFIG[tableType] || [];
      } catch (error) {
        console.error(`Error fetching column config for ${tableType}:`, error);
        // Fall back to placeholder data on error
        return PLACEHOLDER_COLUMN_CONFIG[tableType] || [];
      }
    },
    refetchOnWindowFocus: false
  });

  const updateColumnConfigData = async (newConfig: ColumnConfigItem[]): Promise<boolean> => {
    try {
      // Show success toast without actual DB updates since workspace context is removed
      toast.success(`Column configuration for ${tableType} saved successfully (demo mode)`);
      refetch();
      return true;
    } catch (error) {
      console.error('Error updating column config:', error);
      toast.error(`Failed to save column configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  };

  return {
    columnConfig: columnConfig || [],
    isLoading,
    error,
    isAdmin,
    updateColumnConfig: updateColumnConfigData
  };
}
